import { useState, type MouseEvent } from 'react'
import { AlertTriangle, Loader2, Megaphone, Plus, Settings2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { CampaignSettingsDialog } from '@/components/campaigns/CampaignSettingsDialog'
import { OptOutReviews } from '@/components/campaigns/OptOutReviews'
import { LoadError } from '@/components/campaigns/LoadError'
import { CampaignStatusChip } from '@/components/campaigns/StatusChip'
import { formatDateTime, formatNumber } from '@/components/campaigns/campaignFormat'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useCampaignSettingsQuery, useCrmCampaignsQuery, useDeleteCampaign } from '@/queries'
import type { CrmCampaign } from '@/services/crmCampaignsService'

export function CampaignsPage() {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const campaigns = useCrmCampaignsQuery()
  const settings = useCampaignSettingsQuery()
  const deleteCampaign = useDeleteCampaign()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [toDelete, setToDelete] = useState<CrmCampaign | null>(null)

  const closeDelete = () => {
    setToDelete(null)
    deleteCampaign.reset()
  }

  const confirmDelete = (event: MouseEvent) => {
    // El diálogo sigue abierto si falla, para enseñar el motivo.
    event.preventDefault()
    if (toDelete) deleteCampaign.mutate(toDelete.id, { onSuccess: closeDelete })
  }

  return (
    <div className="container mx-auto space-y-8 p-4 md:p-6">
      <PageHeader title={t('campaigns.list.title')} description={t('campaigns.list.description')}>
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          <Settings2 className="mr-2 h-4 w-4" />
          {t('campaigns.list.settings')}
        </Button>
        <Button asChild>
          <Link to={`/${lang}/campaigns/new`}>
            <Plus className="mr-2 h-4 w-4" />
            {t('campaigns.list.new')}
          </Link>
        </Button>
      </PageHeader>

      {settings.data?.despacho_activo === false ? (
        <Alert className="border-warning/30 bg-warning/10">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('campaigns.list.dispatch_off')}</AlertDescription>
        </Alert>
      ) : null}

      <OptOutReviews />

      {campaigns.isPending ? (
        <div className="space-y-2" data-testid="campaigns-loading">
          {[0, 1, 2, 3].map((row) => (
            <Skeleton key={row} className="h-10 w-full" />
          ))}
        </div>
      ) : campaigns.isError ? (
        <LoadError error={campaigns.error} onRetry={() => void campaigns.refetch()} />
      ) : campaigns.data.length === 0 ? (
        <EmptyState
          icon={<Megaphone className="h-6 w-6" />}
          title={t('campaigns.list.empty_title')}
          description={t('campaigns.list.empty_description')}
        >
          <Button asChild>
            <Link to={`/${lang}/campaigns/new`}>{t('campaigns.list.new')}</Link>
          </Button>
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('campaigns.list.col_name')}</TableHead>
                <TableHead>{t('campaigns.list.col_status')}</TableHead>
                <TableHead className="text-right">{t('campaigns.list.col_recipients')}</TableHead>
                <TableHead className="text-right">{t('campaigns.list.col_sent')}</TableHead>
                <TableHead className="text-right">{t('campaigns.list.col_replied')}</TableHead>
                <TableHead>{t('campaigns.list.col_date')}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.data.map((campaign) => (
                <TableRow key={campaign.id} className={cn(campaign.estado === 'archivada' && 'opacity-60')}>
                  <TableCell className="font-medium">
                    <Link
                      className="hover:underline"
                      to={
                        campaign.estado === 'borrador'
                          ? `/${lang}/campaigns/${campaign.id}/edit`
                          : `/${lang}/campaigns/${campaign.id}`
                      }
                    >
                      {campaign.nombre}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <CampaignStatusChip status={campaign.estado} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(campaign.destinatarios, lang)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(campaign.enviados, lang)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(campaign.respondidos, lang)}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDateTime(campaign.lanzada_at ?? campaign.created_at, lang)}
                  </TableCell>
                  <TableCell className="text-right">
                    {campaign.estado === 'borrador' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('campaigns.list.delete.aria', { name: campaign.nombre })}
                        onClick={() => setToDelete(campaign)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={toDelete !== null} onOpenChange={(open) => !open && closeDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('campaigns.list.delete.title', { name: toDelete?.nombre ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('campaigns.list.delete.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          {deleteCampaign.isError ? (
            <Alert variant="destructive">
              <AlertDescription>{deleteCampaign.error.message}</AlertDescription>
            </Alert>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('campaigns.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={deleteCampaign.isPending} onClick={confirmDelete}>
              {deleteCampaign.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('campaigns.list.delete.button')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CampaignSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  )
}
